// Plivo segment calculation utilities (GSM-7 charset based)
// This module provides functions to calculate SMS message segments according to Plivo's encoding rules

// GSM-7 character set (GSM 03.38)
const GSM_7BIT_CHARS = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà^{}\\[~]|€";

/**
 * Checks if a string contains only GSM-7 characters
 * @param {string} str - The string to check
 * @returns {boolean} - True if all characters are GSM-7, false otherwise
 */
export function isGsm7(str) {
  for (const ch of Array.from(str || '')) {
    if (!GSM_7BIT_CHARS.includes(ch)) return false;
  }
  return true;
}

/**
 * Calculates the number of SMS segments required for a message according to Plivo's rules
 * 
 * Rules:
 * - GSM-7 encoding: 160 chars per segment (single), 153 chars per segment (concatenated)
 * - UCS-2/Unicode encoding: 70 chars per segment (single), 67 chars per segment (concatenated)
 * - Newlines (\n) are converted to CRLF (\r\n) and count as 2 characters
 * - Escape sequence characters in GSM-7 ({ } [ ] \ | ~ ^ €) count as 2 characters each
 * 
 * @param {string} message - The message text
 * @returns {number} - The number of segments required
 */
export function getPlivoSegmentCount(message) {
  const msg = message || '';
  const unicode = !isGsm7(msg);
  // Plivo counts Unicode messages in UCS-2 (UTF-16 code units).
  // Use UTF-16 code unit length and count CRLF (\r\n) for newlines.
  const msgWithCRLF = msg.replace(/\n/g, '\r\n');
  
  let charCount;
  if (unicode) {
    // For Unicode messages, use UTF-16 code unit length directly
    charCount = msgWithCRLF.length;
  } else {
    // For GSM-7 messages, count escape sequence characters as 2 characters each
    // Escape sequence characters: { } [ ] \ | ~ ^ €
    const escapeChars = /[{}[\]\\|~\^€]/g;
    const escapeCharCount = (msgWithCRLF.match(escapeChars) || []).length;
    // Each escape character counts as 2, so we add the count of escape chars
    charCount = msgWithCRLF.length + escapeCharCount;
  }
  
  const segmentSize = unicode ? 70 : 160;
  if (charCount <= segmentSize) return 1;
  const concatSize = unicode ? 67 : 153;
  return Math.ceil(charCount / concatSize);
}

/**
 * Counts message characters for display purposes
 * - Counts CRLF as 2 characters
 * - Counts escape sequence chars as 2 (for GSM-7 messages)
 * - Counts emojis as single characters
 * 
 * @param {string} message - The message text
 * @returns {number} - The character count for display
 */
export function countMessageChars(message) {
  if (!message) return 0;
  const isGsm7Message = isGsm7(message);
  let length = 0;
  for (let i = 0; i < message.length; i++) {
    if (message[i] === '\n') {
      length += 2;
    } else if (isGsm7Message && /[{}[\]\\|~\^€]/.test(message[i])) {
      // Escape sequence characters in GSM-7 count as 2
      length += 2;
    } else {
      length += 1;
    }
  }
  return length;
}


